import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import BR from '@/assets/flags/BR.svg'
import EN from '@/assets/flags/EN.svg'
import ES from '@/assets/flags/ES.svg'

export const useLanguageStore = defineStore('language', () => {
  // Estado reativo
  const systemLanguage = ref('pt-BR')
  const processingLanguage = ref('pt-BR')
  let i18nInstance = null
  const availableLanguages = ref([
    {
      code: 'pt-BR',
      name: 'Português (Brasil)',
      flag: BR,
    },
    { code: 'en-US', name: 'English (US)', flag: EN },
    {
      code: 'es-ES',
      name: 'Español (España)',
      flag: ES,
    },
  ])

  // Getters computados
  const currentSystemLanguage = computed(() => systemLanguage.value)
  const currentProcessingLanguage = computed(() => processingLanguage.value)
  const availableSystemLanguages = computed(() => availableLanguages.value)
  const availableProcessingLanguages = computed(() => availableLanguages.value)

  // Actions
  const initializeLanguages = () => {
    try {
      const token = window.tokenParsed

      // 1. Primeiro verificar se existe configuração salva no localStorage
      if (token?.sub) {
        const savedConfig = getSavedLanguageConfig(token.sub)
        if (savedConfig) {
          systemLanguage.value = savedConfig.systemLanguage || 'pt-BR'
          processingLanguage.value = savedConfig.processingLanguage || 'pt-BR'
          return // Se encontrou configuração salva, usar ela e sair
        }
      }

      // 2. Se não existe configuração salva, verificar se existe no token
      if (token?.locale) {
        systemLanguage.value = token.locale
        processingLanguage.value = token.locale
      } else {
        // 3. Fallback para pt-BR se não existe no token
        systemLanguage.value = 'pt-BR'
        processingLanguage.value = 'pt-BR'
      }

      // 4. Salvar configuração inicial
      if (token?.sub) {
        saveLanguageConfig(token.sub)
      }

      // 5. Sincronizar com i18n após inicialização (com delay para garantir que o i18n esteja pronto)
      setTimeout(() => {
        syncWithI18n()
      }, 100)
    } catch (error) {
      console.error('Erro ao inicializar idiomas:', error)
      // Fallback para pt-BR em caso de erro
      systemLanguage.value = 'pt-BR'
      processingLanguage.value = 'pt-BR'
    }
  }

  const setSystemLanguage = (language) => {
    systemLanguage.value = language
    saveCurrentConfig()
    syncWithI18n()
  }

  const setProcessingLanguage = (language) => {
    processingLanguage.value = language
    saveCurrentConfig()
  }

  // Método para definir a instância do i18n
  const setI18nInstance = (instance) => {
    i18nInstance = instance
  }

  // Método para sincronizar com i18n
  const syncWithI18n = () => {
    try {
      if (i18nInstance) {
        i18nInstance.global.locale.value = systemLanguage.value
        return
      }

      // Fallback: tentar diferentes formas de acessar o i18n
      let fallbackI18n = null

      // 1. Tentar via window.$i18n
      if (window.$i18n && window.$i18n.global) {
        fallbackI18n = window.$i18n.global
      }

      // 2. Tentar via document.querySelector('#app').__vue_app__
      if (!fallbackI18n && document.querySelector('#app')) {
        const app = document.querySelector('#app').__vue_app__
        if (
          app &&
          app.config &&
          app.config.globalProperties &&
          app.config.globalProperties.$i18n
        ) {
          fallbackI18n = app.config.globalProperties.$i18n.global
        }
      }

      if (fallbackI18n) {
        fallbackI18n.locale.value = systemLanguage.value
      } else {
        console.log(
          '🌐 [Language Store] i18n não disponível, tentando novamente em 200ms',
        )
        // Tentar novamente após um delay
        setTimeout(() => {
          syncWithI18n()
        }, 200)
      }
    } catch (error) {
      console.error('Erro ao sincronizar com i18n:', error)
    }
  }

  const getSavedLanguageConfig = (userId) => {
    try {
      const key = `language_config_${userId}`
      const saved = localStorage.getItem(key)
      return saved ? JSON.parse(saved) : null
    } catch (error) {
      console.error('Erro ao recuperar configuração de idioma:', error)
      return null
    }
  }

  const saveLanguageConfig = (userId) => {
    try {
      const key = `language_config_${userId}`
      const config = {
        systemLanguage: systemLanguage.value,
        processingLanguage: processingLanguage.value,
        lastUpdated: new Date().toISOString(),
      }
      localStorage.setItem(key, JSON.stringify(config))
    } catch (error) {
      console.error('Erro ao salvar configuração de idioma:', error)
    }
  }

  const saveCurrentConfig = () => {
    try {
      const token = window.tokenParsed
      if (token && token.sub) {
        saveLanguageConfig(token.sub)
      }
    } catch (error) {
      console.error('Erro ao salvar configuração atual:', error)
    }
  }

  const resetToDefault = () => {
    const token = window.tokenParsed
    if (token && token.locale) {
      systemLanguage.value = token.locale
      processingLanguage.value = token.locale
    } else {
      systemLanguage.value = 'pt-BR'
      processingLanguage.value = 'pt-BR'
    }
    saveCurrentConfig()
  }

  return {
    // Estado
    systemLanguage,
    processingLanguage,
    availableLanguages,

    // Getters
    currentSystemLanguage,
    currentProcessingLanguage,
    availableSystemLanguages,
    availableProcessingLanguages,

    // Actions
    initializeLanguages,
    setSystemLanguage,
    setProcessingLanguage,
    resetToDefault,
    syncWithI18n,
    setI18nInstance,
  }
})
